import { useState } from 'react';

export const IG_EXPERIMENT_NAME = 'ig_landing_v1';
export const IG_TREATMENTS = ['a', 'b', 'c', 'd', 'e1', 'e2', 'e3'];

const ASSIGNMENT_KEY = `${IG_EXPERIMENT_NAME}_treatment`;
const QA_KEY = `${IG_EXPERIMENT_NAME}_qa_treatment`;

function equalTreatmentWeights() {
  return Object.fromEntries(IG_TREATMENTS.map((treatment) => [treatment, 1]));
}

export function parseInstagramTreatmentWeights(value) {
  if (!value?.trim()) return equalTreatmentWeights();

  const weights = {};
  const entries = value.split(',').map((entry) => entry.trim()).filter(Boolean);

  for (const entry of entries) {
    const [treatment, rawWeight, ...extra] = entry.split(':').map((part) => part.trim());
    const weight = Number(rawWeight);

    if (extra.length || !IG_TREATMENTS.includes(treatment) || treatment in weights ||
        rawWeight === '' || !Number.isFinite(weight) || weight < 0) {
      return equalTreatmentWeights();
    }

    weights[treatment] = weight;
  }

  const hasEveryTreatment = IG_TREATMENTS.every((treatment) => treatment in weights);
  const totalWeight = Object.values(weights).reduce((total, weight) => total + weight, 0);
  return hasEveryTreatment && totalWeight > 0 ? weights : equalTreatmentWeights();
}

export function isPaidInstagramVisit(search) {
  const params = new URLSearchParams(search);
  const source = params.get('utm_source')?.toLowerCase();
  const medium = params.get('utm_medium')?.toLowerCase();

  return (source === 'ig' || source === 'instagram') &&
    (medium === 'paid' || medium === 'paid_social');
}

export function chooseInstagramTreatment(
  randomValue = Math.random(),
  weights = parseInstagramTreatmentWeights(process.env.REACT_APP_IG_EXPERIMENT_WEIGHTS)
) {
  const totalWeight = IG_TREATMENTS.reduce((total, treatment) => total + weights[treatment], 0);
  const boundedRandom = Number.isFinite(randomValue)
    ? Math.min(Math.max(0, randomValue), 1 - Number.EPSILON)
    : 0;
  const target = boundedRandom * totalWeight;
  let cumulativeWeight = 0;

  for (const treatment of IG_TREATMENTS) {
    cumulativeWeight += weights[treatment];
    if (target < cumulativeWeight) return treatment;
  }

  return IG_TREATMENTS[IG_TREATMENTS.length - 1];
}

function isEnabledTreatment(treatment, weights) {
  return IG_TREATMENTS.includes(treatment) && weights[treatment] > 0;
}

export function parseLockedMobileTreatment(value) {
  const treatment = value?.trim()?.toLowerCase();
  return IG_TREATMENTS.includes(treatment) ? treatment : null;
}

export function isMobileExperimentClient(win = window) {
  if (!win) return false;

  const uaDataMobile = win.navigator?.userAgentData?.mobile;
  if (uaDataMobile === true) return true;

  if (typeof win.matchMedia === 'function') {
    const viewportMobile = win.matchMedia('(max-width: 900px)').matches;
    const coarsePointer = win.matchMedia('(pointer: coarse)').matches;
    if (viewportMobile || coarsePointer) return true;
  }

  if (typeof win.innerWidth === 'number' && win.innerWidth <= 900) {
    return true;
  }

  const userAgent = win.navigator?.userAgent || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(userAgent);
}

export function isLocalExperimentHost(hostname) {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;

  const octets = hostname.split('.').map(Number);
  if (octets.length !== 4 || octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) {
    return false;
  }

  return octets[0] === 10 ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) ||
    (octets[0] === 192 && octets[1] === 168);
}

export function isQaExperimentHost(hostname, nodeEnv = process.env.NODE_ENV) {
  return nodeEnv !== 'production' && isLocalExperimentHost(hostname);
}

function readTreatment(key) {
  try {
    const value = window.sessionStorage.getItem(key);
    return IG_TREATMENTS.includes(value) ? value : null;
  } catch {
    return null;
  }
}

function storeTreatment(key, treatment) {
  try {
    window.sessionStorage.setItem(key, treatment);
  } catch {
    // Session storage can be unavailable in privacy-restricted embedded browsers.
  }
}

export default function useInstagramExperiment(search) {
  const isQa = typeof window !== 'undefined' && isQaExperimentHost(window.location.hostname);
  const experimentEnabled = process.env.REACT_APP_IG_EXPERIMENT_ENABLED === 'true';
  const eligible = isPaidInstagramVisit(search);
  const lockedMobileTreatment = parseLockedMobileTreatment(
    process.env.REACT_APP_IG_MOBILE_LOCKED_TREATMENT
  );
  const lockedDesktopTreatment = parseLockedMobileTreatment(
    process.env.REACT_APP_IG_DESKTOP_LOCKED_TREATMENT
  );
  const isForcedMobileExperience = typeof window !== 'undefined' &&
    Boolean(lockedMobileTreatment) &&
    isMobileExperimentClient(window);
  const isForcedDesktopExperience = typeof window !== 'undefined' &&
    Boolean(lockedDesktopTreatment) &&
    !isMobileExperimentClient(window);

  const [assignedTreatment] = useState(() => {
    if (typeof window === 'undefined') return null;
    if (isForcedMobileExperience) {
      storeTreatment(ASSIGNMENT_KEY, lockedMobileTreatment);
      return lockedMobileTreatment;
    }
    if (isForcedDesktopExperience) {
      storeTreatment(ASSIGNMENT_KEY, lockedDesktopTreatment);
      return lockedDesktopTreatment;
    }
    if (!experimentEnabled || !eligible) return null;

    const weights = parseInstagramTreatmentWeights(process.env.REACT_APP_IG_EXPERIMENT_WEIGHTS);
    const stored = readTreatment(ASSIGNMENT_KEY);
    if (isEnabledTreatment(stored, weights)) return stored;
    const treatment = chooseInstagramTreatment(Math.random(), weights);
    storeTreatment(ASSIGNMENT_KEY, treatment);
    return treatment;
  });
  const [qaTreatment, setQaTreatmentState] = useState(() => readTreatment(QA_KEY) || 'a');

  const setQaTreatment = (treatment) => {
    if (!isQa || !IG_TREATMENTS.includes(treatment)) return;
    storeTreatment(QA_KEY, treatment);
    setQaTreatmentState(treatment);
  };

  return {
    experimentName: IG_EXPERIMENT_NAME,
    isExperiment: isQa || Boolean(assignedTreatment),
    isQa,
    audience: isQa
      ? 'qa'
      : isForcedMobileExperience
        ? 'mobile_default'
        : isForcedDesktopExperience
          ? 'desktop_default'
          : eligible
            ? 'paid_instagram'
            : 'organic',
    treatment: isQa ? qaTreatment : assignedTreatment,
    treatments: IG_TREATMENTS,
    setQaTreatment,
  };
}